import React from 'react'
import { FormattedMessage } from 'react-intl'
import { getUrlFromPath } from 'utilities/url'
import { accentuate } from '../'
import { Section, IconMessage, Heading } from '../s'
import Paragraph from 'components/typography/paragraph'
import Link from 'components/link'
import { OrderedList, ListItem } from 'components/list'
import {
    CheckCircleFilled as Success,
    InfoCircleFilled as Info,
    AlertTriangleFilled as Warning
} from 'components/feather'

const FortnoxSupportedFeatures = ({ type }) => (
    <Section>
        <Heading>
            <FormattedMessage
                id="integration_heading_supported_features"
                defaultMessage="Supported features" />
        </Heading>
        <OrderedList>
            <ListItem>
                <IconMessage className="success">
                    <Success size={20} />
                    <div>
                        <Paragraph>
                            <strong>
                                <FormattedMessage
                                    id="integration_fortnox_feature_heading_sync_users"
                                    defaultMessage="Synchronize users between Fortnox and Huma" />
                            </strong>
                            <br />
                            <FormattedMessage
                                id="integration_fortnox_feature_text_sync_users"
                                defaultMessage="When synchronizing users manually, Huma will compare the users in Fortnox with users in your Huma organization. Decide what to do with non-matching and missing values, as well as with users that don’t exist in both systems." />
                        </Paragraph>
                        <OrderedList>
                            <ListItem>
                                <IconMessage className="warning">
                                    <Warning size={20} />
                                    <div>
                                        <Paragraph>
                                            <FormattedMessage
                                                id="integration_fortnox_feature_text_resync_add"
                                                defaultMessage="You need to <accent>resynchronize</accent> to add new users. They will not be added automatically."
                                                values={{ accent: accentuate }} />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                        </OrderedList>
                    </div>
                </IconMessage>
            </ListItem>
            <ListItem>
                <IconMessage className="success">
                    <Success size={20} />
                    <div>
                        <Paragraph>
                            <strong>
                                <FormattedMessage
                                    id="integration_fortnox_feature_heading_fields_in_sync"
                                    defaultMessage="Keep profile fields updated from Huma to Fortnox in real-time" />
                            </strong>
                            <br />
                            <FormattedMessage
                                id="integration_fortnox_feature_text_fields_in_sync"
                                defaultMessage="Changes made in the supported profile fields (<link>listed below</link>) will be automatically updated in Fortnox."
                                values={{ link: chunks => (
                                    <Link to={getUrlFromPath('path:integrations.integration.details')({ type })}>
                                        {chunks}
                                    </Link>
                                ) }} />
                        </Paragraph>
                        <OrderedList>
                            <ListItem>
                                <IconMessage className="warning">
                                    <Warning size={20} />
                                    <div>
                                        <Paragraph>
                                            <FormattedMessage
                                                id="integration_fortnox_feature_text_resync_updates"
                                                defaultMessage="Note that changes made in Fortnox will not be automatically updated in Huma. To do that, you’ll need to manually <accent>synchronize</accent> once again."
                                                values={{ accent: accentuate }} />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                        </OrderedList>
                    </div>
                </IconMessage>
            </ListItem>
            <ListItem>
                <IconMessage className="success">
                    <Success size={20} />
                    <div>
                        <Paragraph>
                            <strong>
                                <FormattedMessage
                                    id="integration_fortnox_feature_heading_send_absence_data"
                                    defaultMessage="Send absence from Huma to Fortnox" />
                            </strong>
                            <br />
                            <FormattedMessage
                                id="integration_fortnox_feature_text_send_absence_data"
                                defaultMessage="Registered and approved absence from Huma can be transferred to Fortnox and assigned corresponding absence types there. This is configured in the <accent>Map absence types</accent> section under integration settings."
                                values={{ accent: accentuate }} />
                        </Paragraph>
                        <OrderedList>
                            <ListItem>
                                <IconMessage className="warning">
                                    <Warning size={20} />
                                    <div>
                                        <Paragraph>
                                            <FormattedMessage
                                                id="integration_fortnox_feature_text_absence_changes"
                                                defaultMessage="Changes in absence in Huma will result in changes to the absence created in Fortnox." />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                            <ListItem>
                                <IconMessage className="warning">
                                    <Warning size={20} />
                                    <div>
                                        <Paragraph>
                                            <FormattedMessage
                                                id="integration_fortnox_feature_text_absence_deletion"
                                                defaultMessage="If absence is deleted in Huma, it will also be deleted in Fortnox." />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                            <ListItem>
                                <IconMessage className="info">
                                    <Info size={20} />
                                    <div>
                                        <Paragraph>
                                            <FormattedMessage
                                                id="integration_fortnox_quirk_text_rate_limit"
                                                defaultMessage="Due to rate limiting in Fortnox’ API, we currently do not send absences lasting longer than 50 working days." />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                            <ListItem>
                                <IconMessage className="info">
                                    <Info size={20} />
                                    <div>
                                        <Paragraph className="compact">
                                            <FormattedMessage
                                                id="integration_fortnox_quirk_default_work_schedule"
                                                defaultMessage="New employees created in Fortnox by this integration will start with the default 0-hour work schedule, and will need to be assigned to a work schedule with hours set for the <link>absence integration to work correctly<\/link>."
                                                values={{ link: chunks => (
                                                    <Link
                                                        href="https://support.fortnox.se/sv_SE/kalender/franvaro-vid-nollschema"
                                                        target="_blank">
                                                        {chunks}
                                                    </Link>
                                                ) }} />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                        </OrderedList>
                    </div>
                </IconMessage>
            </ListItem>
        </OrderedList>
    </Section>
)

export default FortnoxSupportedFeatures